import { useState } from "react";
import { Head } from "@inertiajs/react";
import {
  Container,
  Stack,
  Group,
  Box,
  Grid,
  Button,
  Textarea,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import AppShell from "../Components/Layout/AppShell";
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  MainText,
  DimmedText,
} from "../Components/UI/Typography";
import { Input, Select, MultiSelect } from "../Components/UI/Forms";
import { Card, Badge, Drawer } from "../Components/UI";
import PhotoShowcase from "../Components/UI/PhotoShowcase";

export default function UIShowcase() {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);
  const [inputValue, setInputValue] = useState("");
  const [selectValue, setSelectValue] = useState<string | null>(null);
  const [multiSelectValue, setMultiSelectValue] = useState<string[]>([]);

  // Sample photos for showcase
  const samplePhotos = [
    {
      id: 1,
      url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=800&fit=crop",
      title: "Portrait Session",
      description: "Professional headshot photography",
    },
    {
      id: 2,
      url: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=800&h=800&fit=crop",
      title: "Creative Portrait",
      description: "Artistic portrait photography",
    },
    {
      id: 3,
      url: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&h=800&fit=crop",
      title: "Studio Session",
      description: "Studio lighting photography",
    },
    {
      id: 4,
      url: "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=800&h=800&fit=crop",
      title: "Outdoor Portrait",
      description: "Natural light outdoor photography",
    },
    {
      id: 5,
      url: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=800&h=800&fit=crop",
      title: "Fashion Shoot",
      description: "Fashion and editorial photography",
    },
    {
      id: 6,
      url: "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=800&h=800&fit=crop",
      title: "Lifestyle Portrait",
      description: "Casual lifestyle photography",
    },
  ];

  return (
    <AppShell>
      <Head title="UI Components Showcase">
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <Box
        style={{
          minHeight: "100vh",
          background:
            "linear-gradient(180deg, #0a0a0b 0%, #111113 50%, #0a0a0b 100%)",
          padding: "2rem 0",
        }}
      >
        <Container size="xl">
          <Stack gap="xl">
            {/* Header */}
            <Stack gap="md" align="center" mb="xl">
              <Heading1>UI Components Showcase</Heading1>
              <DimmedText>
                A comprehensive suite of themed UI components built with Mantine
              </DimmedText>
            </Stack>

            {/* Typography Section */}
            <Card padding="xl" radius="md">
              <Stack gap="lg">
                <Heading2>Typography</Heading2>
                <MainText>
                  Typography components provide consistent text styling
                  throughout your application.
                </MainText>

                <Stack gap="md">
                  <Heading1>Heading 1 - Main Title</Heading1>
                  <Heading2>Heading 2 - Section Title</Heading2>
                  <Heading3>Heading 3 - Subsection Title</Heading3>
                  <Heading4>Heading 4 - Card Title</Heading4>
                  <Heading5>Heading 5 - Small Title</Heading5>
                  <Heading6>Heading 6 - Tiny Title</Heading6>
                  <MainText>
                    Main Text - This is the primary text component with white
                    color for main content.
                  </MainText>
                  <DimmedText>
                    Dimmed Text - This is secondary text with a dimmed color for
                    supporting information.
                  </DimmedText>
                </Stack>
              </Stack>
            </Card>

            {/* Form Components Section */}
            <Card padding="xl" radius="md">
              <Stack gap="lg">
                <Heading2>Form Components</Heading2>
                <MainText>
                  Form components with consistent theming and validation support.
                </MainText>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap="md">
                      <Input
                        label="Text Input"
                        placeholder="Enter your name"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.currentTarget.value)}
                      />
                      <Textarea
                        label="Textarea"
                        placeholder="Enter your message"
                        minRows={4}
                        styles={{
                          label: {
                            color: "white",
                            marginBottom: "0.5rem",
                            fontWeight: 500,
                          },
                          input: {
                            backgroundColor: "rgba(26, 27, 30, 0.95)",
                            border: "1px solid rgba(55, 58, 64, 0.4)",
                            color: "white",
                            "&:focus": {
                              borderColor: "#f97316",
                              boxShadow: "0 0 0 1px rgba(249, 115, 22, 0.3)",
                            },
                            "&::placeholder": {
                              color: "rgba(161, 161, 170, 0.5)",
                            },
                          },
                        }}
                      />
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <Stack gap="md">
                      <Select
                        label="Select Dropdown"
                        placeholder="Choose an option"
                        data={[
                          { value: "react", label: "React" },
                          { value: "vue", label: "Vue" },
                          { value: "angular", label: "Angular" },
                          { value: "svelte", label: "Svelte" },
                        ]}
                        value={selectValue}
                        onChange={setSelectValue}
                      />
                      <MultiSelect
                        label="Multi-Select"
                        placeholder="Choose multiple options"
                        data={[
                          { value: "typescript", label: "TypeScript" },
                          { value: "javascript", label: "JavaScript" },
                          { value: "python", label: "Python" },
                          { value: "ruby", label: "Ruby" },
                          { value: "go", label: "Go" },
                        ]}
                        value={multiSelectValue}
                        onChange={setMultiSelectValue}
                      />
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>

            {/* Card & Badge Section */}
            <Card padding="xl" radius="md">
              <Stack gap="lg">
                <Heading2>Cards & Badges</Heading2>
                <MainText>
                  Card components for content containers and badges for labels.
                </MainText>

                <Grid>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Card padding="md" radius="md">
                      <Stack gap="sm">
                        <Heading4>Card Example</Heading4>
                        <MainText size="sm">
                          This is a themed card component with consistent
                          styling.
                        </MainText>
                        <Group gap="xs">
                          <Badge variant="orange">New</Badge>
                          <Badge variant="outline">Featured</Badge>
                        </Group>
                      </Stack>
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Card padding="md" radius="md">
                      <Stack gap="sm">
                        <Heading4>Badge Variants</Heading4>
                        <Group gap="xs" wrap="wrap">
                          <Badge variant="default">Default</Badge>
                          <Badge variant="orange">Orange</Badge>
                          <Badge variant="outline">Outline</Badge>
                          <Badge variant="light">Light</Badge>
                        </Group>
                      </Stack>
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, md: 4 }}>
                    <Card padding="md" radius="md">
                      <Stack gap="sm">
                        <Heading4>Interactive Card</Heading4>
                        <MainText size="sm">
                          Cards can be interactive with hover effects and
                          click handlers.
                        </MainText>
                        <Button variant="filled" color="orange" fullWidth>
                          Action Button
                        </Button>
                      </Stack>
                    </Card>
                  </Grid.Col>
                </Grid>
              </Stack>
            </Card>

            {/* Drawer Section */}
            <Card padding="xl" radius="md">
              <Stack gap="lg">
                <Heading2>Drawer</Heading2>
                <MainText>
                  Drawer component for slide-out panels and side navigation.
                </MainText>
                <Button onClick={openDrawer} variant="filled" color="orange">
                  Open Drawer
                </Button>
              </Stack>
            </Card>

            {/* Photo Showcase Section */}
            <Card padding="xl" radius="md">
              <Stack gap="lg">
                <Heading2>Photo Showcase</Heading2>
                <MainText>
                  Photo showcase component for displaying photoshoots with
                  lightbox functionality.
                </MainText>
                <PhotoShowcase
                  photos={samplePhotos}
                  title="Sample Photoshoot Gallery"
                  columns={3}
                />
              </Stack>
            </Card>
          </Stack>
        </Container>
      </Box>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="Example Drawer"
        position="right"
        size="md"
      >
        <Stack gap="md">
          <Heading3>Drawer Content</Heading3>
          <MainText>
            This is an example drawer component. You can place any content here.
          </MainText>
          <Input label="Name" placeholder="Enter name" />
          <Select
            label="Category"
            placeholder="Select category"
            data={[
              { value: "1", label: "Option 1" },
              { value: "2", label: "Option 2" },
            ]}
          />
          <Button variant="filled" color="orange" fullWidth>
            Submit
          </Button>
        </Stack>
      </Drawer>
    </AppShell>
  );
}

